import { LogOut, Mail, Shield, User } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, LanguageToggle, ThemeToggle } from '@solara/ui-shared';
import { useI18n } from '@solara/i18n';
import { useAuth } from '../auth/AuthContext';
import { useSession } from '../auth/SessionContext';

export default function ProfilePage() {
  const { t, locale } = useI18n();
  const { currentUser } = useAuth();
  const { signOut, user } = useSession();
  // Prefer the real JWT-backed user; fall back to the demo persona until
  // `/auth/me` resolves.
  const email = user?.email ?? currentUser?.email;
  const fullName = user
    ? `${user.givenName} ${user.familyName}`.trim()
    : [currentUser?.givenName, currentUser?.familyName].filter(Boolean).join(' ');
  const role = user?.role ?? currentUser?.role;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="text-fg-muted text-sm">Account details, preferences, sign-out.</p>
      </header>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Account
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Row icon={<Mail className="h-4 w-4" />} label="Email">
            <code className="rounded bg-surface-muted px-2 py-1 text-xs">{email ?? '—'}</code>
          </Row>
          <Row icon={<User className="h-4 w-4" />} label="Name">
            <span>{fullName || '—'}</span>
          </Row>
          <Row icon={<Shield className="h-4 w-4" />} label="Role">
            <code className="rounded bg-surface-muted px-2 py-1 text-xs">{role ?? '—'}</code>
          </Row>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Row label={t('language.english') + ' / ' + t('language.arabic')}>
            <LanguageToggle />
          </Row>
          <Row label="Theme">
            <ThemeToggle />
          </Row>
          <Row label="Active locale">
            <code className="rounded bg-surface-muted px-2 py-1 text-xs">{locale}</code>
          </Row>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Session</CardTitle>
        </CardHeader>
        <CardBody>
          <Button variant="outline" onClick={signOut} className="gap-2">
            <LogOut className="h-4 w-4 rtl-mirror" />
            Sign out
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}

function Row({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-fg-muted inline-flex items-center gap-2">
        {icon}
        {label}
      </span>
      {children}
    </div>
  );
}
